import axios from "axios";
import { store } from "~/store";
import { Firebase } from "~/services/firebase";
import { signInAnonymously, onAuthStateChanged } from "firebase/auth";
import { handleError } from "~/utils/handleError";
import authAction from "~/store/actions/authAction";

const isInForeground = () => document.visibilityState === 'visible';

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const MAX_RETRIES = 3;

// Resolve uma única vez quando o Firebase termina de restaurar a sessão do IndexedDB.
// Necessário para o caso de acesso direto por link (deep link) onde o Redux ainda está vazio.
const firebaseAuthReady = new Promise((resolve) => {
  const unsubscribe = onAuthStateChanged(Firebase, () => {
    unsubscribe();
    resolve();
  });
});

const clearUserSession = () => {
  const userKeys = [
    "@prit-web/accessToken",
    "@prit-web/accessTokenExpirationTime",
    "@prit-web/userId",
    "@prit-web/meData",
    "@prit-web/TypeLogin",
    "@prit-web/justAuthenticated",
  ];
  userKeys.forEach((key) => localStorage.removeItem(key));
  store.dispatch(authAction.logoutUser());
  window.location.href = "/business/link";
};


export function createdApi(baseURL, defaultHeaders = {}) {
  const api = axios.create({
    baseURL: baseURL,
    headers: {
      "content-type": "application/json",
      ...defaultHeaders
    },
  });

api.interceptors.request.use(async (config) => {
  if (!navigator.onLine) {
    return Promise.reject({ message: 'Sem conexão com a internet', code: 'NO_CONNECTION' });
  }

  if (!isInForeground()) {
    console.log('Aba em background, aguardando foco para continuar a requisição...');
    await new Promise((resolve) => {
      const handleVisibilityChange = () => {
        if (isInForeground()) {
          document.removeEventListener('visibilitychange', handleVisibilityChange);
          resolve();
        }
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);
    });
  }

  const ignoredRoutes = ["/auth/renew"];

  if (ignoredRoutes.some((route) => config.url?.includes(route))) {
    return config;
  }

  // Tenta o Redux primeiro (caminho rápido — já populado pelo onIdTokenChanged do App.jsx).
  // Se ainda estiver vazio (ex: acesso direto por deep link), aguarda o Firebase restaurar a sessão.
  let authUser = store.getState().auth?.user;
  if (!authUser || authUser.isAnonymous) {
    await firebaseAuthReady;
    authUser = store.getState().auth?.user;
    // Fallback: se Redux ainda não foi atualizado mas Firebase já tem usuário real
    // (race condition entre onIdTokenChanged do App.jsx e o interceptor)
    if ((!authUser || authUser.isAnonymous) && Firebase.currentUser && !Firebase.currentUser.isAnonymous) {
      authUser = Firebase.currentUser;
    }
  }

  if (authUser && !authUser.isAnonymous) {
    try {
      // getIdToken() renova automaticamente o ID token via refresh token do Firebase
      // O usuário NÃO precisa re-autenticar via WhatsApp para isso — só no login inicial
      const freshToken = await authUser.getIdToken();
      config.headers.Authorization = `Bearer ${freshToken}`;
      localStorage.setItem("@prit-web/accessToken", freshToken);
    } catch {
      // Sessão completamente revogada no Firebase — limpa dados e redireciona para login
      clearUserSession();
      return Promise.reject({ message: "Sessão expirada. Faça login novamente.", code: "TOKEN_EXPIRED", isTokenExpired: true });
    }
  } else {
    try {
      const userCredential = await signInAnonymously(Firebase);
      const newToken = await userCredential.user.getIdToken();
      config.headers.Authorization = `Bearer ${newToken}`;
      localStorage.setItem("@prit-web/accessToken", newToken);
    } catch (error) {
      console.error("Erro ao gerar token anônimo:", error);
    }
  }
  config.headers["User-Agent"] = "WebConsumer";
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;

    if (!config) {
      return Promise.reject(error);
    }

    const isNetworkError = !error.response || error.code === 'ECONNABORTED' || error.message === 'Network Error';

    if (isNetworkError) {
      config.__retryCount = config.__retryCount || 0;

      if (config.__retryCount >= MAX_RETRIES) {
        handleError(error, 'services/api.js');
        return Promise.reject(error);
      }

      config.__retryCount += 1;
      await delay(1000 * config.__retryCount);

      if (!navigator.onLine) {
        return Promise.reject({ message: 'Sem conexão na tentativa de retry', code: 'NO_CONNECTION' });
      }

      if (!isInForeground()) {
        await new Promise((resolve) => {
          const handleVisibilityChange = () => {
            if (isInForeground()) {
              document.removeEventListener('visibilitychange', handleVisibilityChange);
              resolve();
            }
          };
          document.addEventListener('visibilitychange', handleVisibilityChange);
        });
      }

      return api(config);
    }

    if (error.response?.status === 401) {
      clearUserSession();
      error.isTokenExpired = true;
      return Promise.reject(error);
    }

    handleError(error, 'services/api.js');
    return Promise.reject(error);
  }
);

  return api;
}

const api = createdApi(process.env.REACT_APP_URL_API);

export default api;

